import * as THREE from 'three';
import { jsPDF } from 'jspdf';
import { isExportHidden } from '../../utils/switchHiddenState';

/**
 * Three.js sahnesindeki mainFrame grubunu vektorel PDF'e donusturur.
 * colorMode: 'color' (renkli) | 'bw' (siyah-beyaz)
 */
class PdfExporter {
  /**
   * @param {THREE.Scene} scene
   * @param {string} colorMode - 'color' | 'bw'
   * @returns {jsPDF} pdf instance
   */
  async generate(scene, colorMode = 'color') {
    const mainFrame = this._findMainFrame(scene);
    if (!mainFrame) {
      throw new Error('MainFrame bulunamadi');
    }

    // Tum matrisleri guncelle
    mainFrame.updateMatrixWorld(true);

    // Frame cerceve polyline'ini (isFrameContent) bulup gercek sinirlarini oku.
    // Bu, mainFrame guncellense bile her zaman canli geometriyi yansitir.
    const frameBounds = this._getFrameBorderBounds(mainFrame);
    const { minX, maxX, minY, maxY } = frameBounds;

    const width = maxX - minX;
    const height = maxY - minY;

    // Three.js birimleri yaklasik 'pt' (point). unit:'mm' kullanildiginda buyuk
    // cerceveler (>5080mm) PDF spec limitini (14400pt) asiyor ve sayfa kesiliyordu.
    // unit:'pt' ile 1:1 esleme yapilir, buyuk cerceveler de sigdirilir.
    const margin = 10; // pt
    const pageW = width + margin * 2;
    const pageH = height + margin * 2;

    const orientation = pageW >= pageH ? 'l' : 'p';
    const pdf = new jsPDF({
      orientation,
      unit: 'pt',
      format: [pageW, pageH],
      compress: true,
    });

    // Arka plan beyaz
    pdf.setFillColor(255, 255, 255);
    pdf.rect(0, 0, pageW, pageH, 'F');

    // Koordinat donusumu: Three.js (Y yukari) -> PDF (Y asagi)
    this._offsetX = -minX + margin;
    this._offsetY = maxY + margin;
    this._colorMode = colorMode;
    this._pdf = pdf;

    // Turkce karakterler icin Unicode TTF (Arial) gom. jsPDF yerlesik fontlari
    // (helvetica/times) WinAnsi encoding kullanip İ ı ğ ş gibi karakterleri
    // bozuyordu (İ→0, ı→1, ş→_). Basarisiz olursa helvetica'ya duser.
    await this._registerFonts(pdf);

    // Sahne traversal
    mainFrame.traverse((child) => {
      if (!child.visible) return;
      // Defpoints katmanini atla
      if (child.userData?.layerName === 'Defpoints') return;

      // Transparan click alanlarini atla
      if (child.material?.opacity !== undefined && child.material.opacity < 0.1) return;
      if (child.name && (child.name.includes('click-hatch') || child.name === 'box-hatch')) return;

      // Gizlenen anahtar (veya exportHidden atasi) — PDF'e cizilmez
      if (isExportHidden(child)) return;

      // Text grubunun glyph mesh'ini atla
      if (child.parent?.userData?.type === 'text') return;

      // Pattern hatch cocuklarini atla
      if (child.parent?.userData?.isPatternHatch) return;

      try {
        // Troika Text (MTEXT glyph mesh'leri) — .text + .fontSize tasir. SDF texture'li
        // (material.map) olduklari icin _drawMesh'te atlaniyorlardi; Notlar/Sembol/Baslik
        // dokumanlarinin metinleri boyle. Once yakala ve vektorel metin olarak ciz.
        if (typeof child.text === 'string' && child.text.length > 0 && typeof child.fontSize === 'number') {
          this._drawTroikaText(child);
        } else if (child.type === 'Line' || child.type === 'LineSegments' || child.type === 'LineLoop') {
          this._drawLine(child);
        } else if (child.type === 'Mesh' && child.geometry) {
          this._drawMesh(child);
        } else if (child.userData?.type === 'text' || child.type === 'Sprite') {
          this._drawText(child);
        }
      } catch (_) {
        // Tek bir entity hata verirse digerlerini durdurma
      }
    });

    return pdf;
  }

  _findMainFrame(scene) {
    let found = null;
    scene.traverse((child) => {
      if (child.userData?.isMainFrame === true && !found) {
        found = child;
      }
    });
    return found;
  }

  /**
   * Uygulamadaki Arial TTF'lerini (Turkce glyph'li) jsPDF'e gomer ve varsayilan
   * font yapar. Basarisiz olursa _hasArial=false kalir, helvetica kullanilir.
   */
  async _registerFonts(pdf) {
    this._hasArial = false;
    const variants = [
      ['/fonts/Arial.ttf', 'PZ-Arial.ttf', 'normal'],
      ['/fonts/ARIALBD.TTF', 'PZ-Arial-Bold.ttf', 'bold'],
      ['/fonts/ARIALI.TTF', 'PZ-Arial-Italic.ttf', 'italic'],
      ['/fonts/ARIALBI.TTF', 'PZ-Arial-BoldItalic.ttf', 'bolditalic'],
    ];
    try {
      let any = false;
      for (const [url, vfs, style] of variants) {
        const b64 = await this._fetchFontBase64(url);
        if (!b64) continue;
        pdf.addFileToVFS(vfs, b64);
        pdf.addFont(vfs, 'Arial', style);
        any = true;
      }
      this._hasArial = any;
      if (any) pdf.setFont('Arial', 'normal');
    } catch (_) {
      this._hasArial = false;
    }
  }

  /** TTF dosyasini fetch edip base64'e cevirir (jsPDF addFileToVFS icin). */
  async _fetchFontBase64(url) {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const buf = await res.arrayBuffer();
      const bytes = new Uint8Array(buf);
      let binary = '';
      const CHUNK = 0x8000;
      for (let i = 0; i < bytes.length; i += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
      }
      return btoa(binary);
    } catch (_) {
      return null;
    }
  }

  /**
   * Frame cerceve polyline'inin (isFrameContent) vertex'lerinden gercek sinirlarini hesapla.
   * mainFrame guncellense bile her zaman canli geometriyi yansitir.
   */
  _getFrameBorderBounds(mainFrame) {
    // isFrameContent child'lari arasinda en son ekleneni bul (reposition yenisini ekler)
    let frameContentGroup = null;
    for (const child of mainFrame.children) {
      if (child.userData?.isFrameContent === true) {
        frameContentGroup = child;
      }
    }

    if (frameContentGroup) {
      const bounds = this._computeBounds(frameContentGroup);
      if (Number.isFinite(bounds.minX) && (bounds.maxX - bounds.minX) > 1) {
        return bounds;
      }
    }

    // Fallback: tum mainFrame'in vertex taramasi
    return this._computeBounds(mainFrame);
  }

  /**
   * Verilen root'un tum cocuklarinin vertex'lerini tarayarak gercek sinirlarini hesapla.
   */
  _computeBounds(root) {
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;
    const v = new THREE.Vector3();

    root.traverse((child) => {
      if (child.geometry?.attributes?.position) {
        const pos = child.geometry.attributes.position;
        const matrix = child.matrixWorld;
        for (let i = 0; i < pos.count; i++) {
          v.fromBufferAttribute(pos, i).applyMatrix4(matrix);
          if (v.x < minX) minX = v.x;
          if (v.x > maxX) maxX = v.x;
          if (v.y < minY) minY = v.y;
          if (v.y > maxY) maxY = v.y;
        }
      }
      // Sprite/text without geometry
      if (child.type === 'Sprite') {
        child.getWorldPosition(v);
        if (v.x < minX) minX = v.x;
        if (v.x > maxX) maxX = v.x;
        if (v.y < minY) minY = v.y;
        if (v.y > maxY) maxY = v.y;
      }
    });

    // Fallback: eger hic vertex bulunamadiysa Box3 kullan
    if (!Number.isFinite(minX)) {
      const box = new THREE.Box3().setFromObject(root);
      return { minX: box.min.x, maxX: box.max.x, minY: box.min.y, maxY: box.max.y };
    }

    return { minX, maxX, minY, maxY };
  }

  // Three.js koordinatini PDF koordinatina cevir
  _tx(x) { return x + this._offsetX; }
  _ty(y) { return this._offsetY - y; }

  _getColor(obj) {
    if (this._colorMode === 'bw') return { r: 0, g: 0, b: 0 };
    const mat = obj.material;
    if (mat && mat.color) {
      return {
        r: Math.round(mat.color.r * 255),
        g: Math.round(mat.color.g * 255),
        b: Math.round(mat.color.b * 255),
      };
    }
    return { r: 0, g: 0, b: 0 };
  }

  _getLineWidth(obj) {
    const lw = obj.material?.linewidth || obj.userData?.lineWidth || 1;
    // Three.js linewidth -> PDF pt (yaklasik donusum)
    return Math.max(0.25, Math.min(lw * 0.4, 5));
  }

  _drawLine(obj) {
    const geo = obj.geometry;
    if (!geo?.attributes?.position) return;

    const pos = geo.attributes.position;
    const count = pos.count;
    if (count < 2) return;

    // World matrix uygula
    obj.updateWorldMatrix(true, false);
    const matrix = obj.matrixWorld;

    const color = this._getColor(obj);
    this._pdf.setDrawColor(color.r, color.g, color.b);
    this._pdf.setLineWidth(this._getLineWidth(obj));

    const v = new THREE.Vector3();

    if (obj.type === 'LineSegments') {
      // Her 2 nokta bir segment
      for (let i = 0; i < count - 1; i += 2) {
        v.fromBufferAttribute(pos, i).applyMatrix4(matrix);
        const x1 = this._tx(v.x), y1 = this._ty(v.y);
        v.fromBufferAttribute(pos, i + 1).applyMatrix4(matrix);
        const x2 = this._tx(v.x), y2 = this._ty(v.y);
        this._pdf.line(x1, y1, x2, y2);
      }
    } else {
      // Line / LineLoop - ardisik noktalar
      const points = [];
      for (let i = 0; i < count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(matrix);
        points.push({ x: this._tx(v.x), y: this._ty(v.y) });
      }
      for (let i = 0; i < points.length - 1; i++) {
        this._pdf.line(points[i].x, points[i].y, points[i + 1].x, points[i + 1].y);
      }
      // LineLoop ise ilk-son noktayi bagla
      if (obj.type === 'LineLoop' && points.length > 2) {
        const last = points[points.length - 1];
        this._pdf.line(last.x, last.y, points[0].x, points[0].y);
      }
    }
  }

  _drawMesh(obj) {
    const geo = obj.geometry;
    if (!geo?.attributes?.position) return;

    // OLE2 frame (gomulu gorsel) — raster olarak PDF'e gom
    if (obj.userData?.type === 'ole2frame' && obj.material?.map) {
      this._drawOleImage(obj);
      return;
    }

    // Texture'lu mesh'leri (text render plane vb.) atla
    if (obj.material?.map) return;

    obj.updateWorldMatrix(true, false);
    const matrix = obj.matrixWorld;
    const pos = geo.attributes.position;
    const index = geo.index;

    const color = this._getColor(obj);
    this._pdf.setFillColor(color.r, color.g, color.b);

    const vA = new THREE.Vector3();
    const vB = new THREE.Vector3();
    const vC = new THREE.Vector3();

    if (index) {
      // Indexed geometry - ucgenleri ciz
      for (let i = 0; i < index.count; i += 3) {
        vA.fromBufferAttribute(pos, index.getX(i)).applyMatrix4(matrix);
        vB.fromBufferAttribute(pos, index.getX(i + 1)).applyMatrix4(matrix);
        vC.fromBufferAttribute(pos, index.getX(i + 2)).applyMatrix4(matrix);
        this._fillTriangle(vA, vB, vC);
      }
    } else {
      // Non-indexed
      for (let i = 0; i < pos.count; i += 3) {
        vA.fromBufferAttribute(pos, i).applyMatrix4(matrix);
        vB.fromBufferAttribute(pos, i + 1).applyMatrix4(matrix);
        vC.fromBufferAttribute(pos, i + 2).applyMatrix4(matrix);
        this._fillTriangle(vA, vB, vC);
      }
    }
  }

  _fillTriangle(a, b, c) {
    const ax = this._tx(a.x), ay = this._ty(a.y);
    const bx = this._tx(b.x), by = this._ty(b.y);
    const cx = this._tx(c.x), cy = this._ty(c.y);

    this._pdf.triangle(ax, ay, bx, by, cx, cy, 'F');
  }

  _drawText(obj) {
    let text = obj.userData?.text || obj.userData?.content || obj.name || '';
    if (!text) return;

    obj.updateWorldMatrix(true, false);
    const worldPos = new THREE.Vector3();
    obj.getWorldPosition(worldPos);

    const x = this._tx(worldPos.x);
    const y = this._ty(worldPos.y);

    // Font boyutu (Three.js birimi ~ pt, jsPDF setFontSize da pt alir)
    let fontSize = obj.userData?.fontSize || obj.userData?.size || 2.5;
    const pdfFontSize = Math.max(4, Math.min(fontSize, 60));

    const color = this._getColor(obj);
    this._pdf.setTextColor(color.r, color.g, color.b);
    this._pdf.setFontSize(pdfFontSize);

    // Rotasyon
    const rotation = obj.userData?.rotation || 0;
    const angle = -rotation * (180 / Math.PI);

    if (Math.abs(angle) > 0.5) {
      this._pdf.text(text, x, y, { angle });
    } else {
      this._pdf.text(text, x, y);
    }
  }

  /**
   * Troika Text mesh'ini (MTEXT segment/satir) vektorel PDF metnine cevirir.
   * Konum, boyut ve renk mesh'in kendisinden okunur; cok satir '\n' ile bolunur.
   */
  _drawTroikaText(obj) {
    const raw = obj.text;
    if (raw == null || raw === '') return;
    const str = String(raw);
    if (!str.trim()) return;

    obj.updateWorldMatrix(true, false);
    const worldPos = new THREE.Vector3();
    obj.getWorldPosition(worldPos);

    // Troika fontSize dunya birimindedir; sayfa 1:1 pt esledigi icin dogrudan pt.
    const fontSize = obj.fontSize || 10;
    const pdfFontSize = Math.max(1.5, Math.min(fontSize, 400));

    const color = this._troikaColor(obj);
    this._pdf.setTextColor(color.r, color.g, color.b);
    this._pdf.setFontSize(pdfFontSize);
    this._applyTroikaFont(obj);

    // Yatay hiza: troika anchorX (blok tutturma noktasi) → jsPDF align.
    const ax = obj.anchorX;
    const align = ax === 'center' ? 'center' : (ax === 'right' ? 'right' : 'left');

    // Dikey hiza: troika anchorY → jsPDF baseline (cok satir bu noktadan asagi akar).
    const ay = String(obj.anchorY || 'top');
    const baseline = ay.indexOf('bottom') === 0 ? 'bottom' : (ay === 'middle' ? 'middle' : 'top');

    // Dunya matrisinden Z rotasyonu; y-down PDF icin isaret ters (bkz. _drawText).
    const e = obj.matrixWorld.elements;
    const angle = -Math.atan2(e[1], e[0]) * (180 / Math.PI);

    const x = this._tx(worldPos.x);
    const y = this._ty(worldPos.y);

    const opt = { baseline, align };
    if (Math.abs(angle) > 0.5) opt.angle = angle;
    // Tek-mesh MTEXT troika'da maxWidth ile satirlanir. Ayni sarmayi jsPDF'e yaptir:
    // maxWidth verilince jsPDF metni '\n' + genislik sarmasiyla cok satira boler ve
    // her satiri align'a gore hizalar. Boylece uygulamadaki cok-satirli gorunum korunur.
    // (Segment tabanli MTEXT child'larinda maxWidth yoktur → yalnizca varsa '\n' satirlari.)
    if (typeof obj.maxWidth === 'number' && isFinite(obj.maxWidth) && obj.maxWidth > 0) {
      opt.maxWidth = obj.maxWidth;
    }
    this._pdf.text(str, x, y, opt);

    // Font stilini varsayilana dondur (sonraki cizimleri etkilemesin).
    this._pdf.setFont(this._hasArial ? 'Arial' : 'helvetica', 'normal');
  }

  /**
   * Troika font path'inden stil (bold/italic) cikarip jsPDF font'unu secer.
   * Gomulu Arial varsa onu (Turkce destekli), yoksa helvetica/times kullanir.
   */
  _applyTroikaFont(obj) {
    const p = String(obj.font || '').toLowerCase();
    const bold = /bd|bi/.test(p);
    const italic = p.endsWith('i.ttf') && !p.endsWith('bd.ttf');
    const style = bold && italic ? 'bolditalic' : (bold ? 'bold' : (italic ? 'italic' : 'normal'));
    if (this._hasArial) {
      try { this._pdf.setFont('Arial', style); return; } catch (_) { /* helvetica'ya dus */ }
    }
    const family = p.indexOf('times') !== -1 ? 'times' : 'helvetica';
    try { this._pdf.setFont(family, style); }
    catch (_) { try { this._pdf.setFont('helvetica', 'normal'); } catch (__) { /* yok say */ } }
  }

  /**
   * OLE2 frame mesh'ini (gomulu gorsel) PDF'e raster image olarak gomer.
   * Texture canvas'indan PNG data URL alinir, jsPDF.addImage ile yerlestirilir.
   */
  _drawOleImage(obj) {
    const tex = obj.material?.map;
    if (!tex) return;

    // Texture kaynak canvas'ini al
    const source = tex.source?.data || tex.image;
    if (!source) return;

    let dataUrl;
    try {
      if (source instanceof HTMLCanvasElement) {
        dataUrl = source.toDataURL('image/png');
      } else if (source instanceof HTMLImageElement) {
        // Image'i canvas'a ciz, dataURL al
        const cvs = document.createElement('canvas');
        cvs.width = source.naturalWidth || source.width;
        cvs.height = source.naturalHeight || source.height;
        const ctx = cvs.getContext('2d');
        ctx.drawImage(source, 0, 0);
        dataUrl = cvs.toDataURL('image/png');
      } else {
        return;
      }
    } catch (_) {
      return; // CORS veya baska hata — atla
    }

    if (!dataUrl || dataUrl === 'data:,') return;

    // Mesh dunya konumu ve boyutunu hesapla
    obj.updateWorldMatrix(true, false);
    const pos = new THREE.Vector3();
    obj.getWorldPosition(pos);

    // PlaneGeometry boyutlari (width, height parametreleri)
    const geo = obj.geometry;
    const params = geo?.parameters;
    let w, h;
    if (params) {
      w = params.width;
      h = params.height;
    } else {
      // Fallback: bounding box'tan hesapla
      const box = new THREE.Box3().setFromObject(obj);
      const size = box.getSize(new THREE.Vector3());
      w = size.x;
      h = size.y;
    }

    // Dunya olcek carpani
    const scale = new THREE.Vector3();
    obj.getWorldScale(scale);
    w *= Math.abs(scale.x);
    h *= Math.abs(scale.y);

    // PDF koordinatlari (sol-ust kose)
    const pdfX = this._tx(pos.x) - w / 2;
    const pdfY = this._ty(pos.y) - h / 2;

    try {
      this._pdf.addImage(dataUrl, 'PNG', pdfX, pdfY, w, h);
    } catch (_) {
      // addImage basarisiz olursa atla
    }
  }

  _troikaColor(obj) {
    if (this._colorMode === 'bw') return { r: 0, g: 0, b: 0 };
    let c;
    try { c = new THREE.Color(obj.color != null ? obj.color : 0x000000); }
    catch (_) { c = new THREE.Color(0, 0, 0); }
    let r = Math.round(c.r * 255), g = Math.round(c.g * 255), b = Math.round(c.b * 255);
    // Beyaz/cok acik metin beyaz zeminde gorunmez → siyaha cevir.
    if (r > 230 && g > 230 && b > 230) { r = 0; g = 0; b = 0; }
    return { r, g, b };
  }
}

export { PdfExporter };
